import React from 'react'
import c from 'classnames'
import { connect } from 'react-redux'
import sanitizeHtml from 'sanitize-html'
import moment from 'moment'

import { money, moneyShort, percent } from 'utils'
import { changeQuote } from 'actions/quoteActions'
import { getQuoteValue } from 'selectors/quoteSelectors'
import { getPermission } from 'selectors/authSelectors'

import { SimpleTextAreaField, DatePicker } from 'discada/dist/Form'

import './QuoteValue.scss'

const PERMS = {
  info: 'deal.fields/info',
  profit: 'deal.fields/profit',
  internal: 'deal.fields/profit',
  customize: 'deal.fields/customize',
}

export const BLUR_KEYS = ['Enter', 'Tab']

export class QuoteValueText extends React.Component {
  constructor(...args) {
    super(...args)

    this.state = {}

    this.formatter = this.props.valueFormatter || (x => x)
  }

  static getDerivedStateFromProps(newProps, oldState) {
    return { ...oldState, ...newProps }
  }

  componentDidMount() {
    this.ref.innerHTML = this.formatter(this.state.value || 0)
  }
  componentDidUpdate() {
    if (document.activeElement !== this.ref)
      this.ref.innerHTML = this.formatter(this.state.value || 0)
  }

  render() {
    let {
        type,
        className,
        onChange,
        valueKey,
        disabled,
        updateValueOnChange,
        processValue = x => x,
      } = this.state,
      proc = ({ target }) => {
        onChange(
          processValue(
            sanitizeHtml(target.innerHTML.toString().replace(/[^\d.-]/g, '')), // TODO this strips alphas too
          ),
        )
      }

    return (
      <span
        contentEditable={!disabled}
        className={c(
          'quote-value',
          `quote-value-${type}`,
          `quote-value-${type}-${valueKey}`,
          className,
        )}
        onKeyDown={({ key }) => BLUR_KEYS.includes(key) && this.ref.blur()}
        onBlur={proc}
        onInput={e => updateValueOnChange && proc(e)}
        ref={r => (this.ref = r)}
      />
    )
  }
}

export function QuoteValueMoney(props) {
  return (
    <QuoteValueText
      {...props}
      processValue={v => (isNaN(v) ? 0 : parseFloat(v))}
      valueFormatter={{ money, moneyShort }[props.type]}
    />
  )
}

export function QuoteValuePercent(props) {
  return (
    <QuoteValueText
      {...props}
      processValue={v => (isNaN(v) ? 0 : parseFloat(v))}
      valueFormatter={percent}
    />
  )
}

export function QuoteValueInput(props) {
  return <QuoteValueText {...props} />
}

export function QuoteValueNumber(props) {
  return <QuoteValueText {...props} processValue={parseFloat} />
}

export class QuoteValueDate extends React.Component {
  constructor(...args) {
    super(...args)

    this.state = {}
  }

  static getDerivedStateFromProps(newProps, oldState) {
    return { ...oldState, ...newProps }
  }
  componentDidMount() {
    this.ref.innerHTML = this.state.value || ''
  }
  componentDidUpdate() {
    this.ref.innerHTML = this.state.value || ''
  }

  render() {
    let { value, label, className, onChange, valueKey, disabled } = this.state,
      proc = v => onChange(v)

    return disabled ? (
      <QuoteValueInput {...this.props} />
    ) : (
      <DatePicker
        onChange={proc}
        label={label}
        value={value || moment().add(7, 'days')}
        className={c(
          'quote-value',
          'quote-value-date',
          `quote-value-date-${valueKey}`,
          className,
        )}
      />
    )
  }
}

export function QuoteValueTextArea(props) {
  let { value, label, className, onChange, valueKey, disabled } = props,
    proc = ({ target }) => {
      onChange(target.value)
    }

  return (
    <SimpleTextAreaField
      disabled={disabled}
      size="small"
      value={value}
      label={label}
      onChange={proc}
      className={c(
        'quote-value',
        'quote-value-textarea',
        `quote-value-textarea-${valueKey}`,
        className,
      )}
    />
  )
}

export function QuoteValueSelect({
  value,
  valueKey,
  className,
  onChange,
  disabled,
  options,
}) {
  let proc = ({ target }) => onChange(target.value)

  return (
    <div
      className={c(
        'select',
        'quote-value',
        'quote-value-select',
        `quote-value-select-${valueKey}`,
        className,
      )}
    >
      <select onChange={proc} disabled={disabled} value={value}>
        {Object.keys(options).map((k, i) => (
          <option key={i} value={k}>
            {options[k]}
          </option>
        ))}
      </select>
    </div>
  )
}

export function QuoteValueBool({ value, className, onChange, disabled }) {
  return (
    <input
      type="checkbox"
      className={c('quote-value', 'quote-value-bool', className, {
        'quote-value-editable': !disabled,
      })}
      onChange={() => onChange(!value)}
      disabled={disabled}
      checked={value}
    />
  )
}

//////////////////////////////////////

const TYPES = {
  text: QuoteValueInput,
  string: QuoteValueInput,
  number: QuoteValueNumber,
  money: QuoteValueMoney,
  moneyShort: QuoteValueMoney,
  select: QuoteValueSelect,
  bool: QuoteValueBool,
  checkbox: QuoteValueBool,
  percent: QuoteValuePercent,
  textarea: QuoteValueTextArea,
  date: QuoteValueDate,
}

export function QuoteValue(props) {
  let {
      id,
      vehicleId,
      value,
      valueKey,
      k,
      label,
      changeQuote,
      type = 'text',
      valueProcessor = v => v,
      readOnly,
      className,
      permit,
      right,
      colorCode,
      forceProgramUpdate,
      onChange,
      onAfterChange = () => {},
      clean,
      dirty,
      track = true,
      squeeze,
    } = props,
    Component = TYPES[type],
    onValueChange =
      onChange ||
      (val => {
        changeQuote(
          vehicleId,
          id,
          {
            [k || valueKey]: valueProcessor(val),
          },
          { squeeze, clean, dirty, forceProgramUpdate, track },
        )
        onAfterChange()
      })

  return (
    <span
      className={c(
        'quote-value-wrapper',
        `quote-value-wrapper-${type}`,
        `quote-value-wrapper-${permit}`,
        {
          'read-only': readOnly,
          'quote-value-wrapper-right':
            right || type === 'number' || type === 'money',
          'color-code': colorCode,
          positive: typeof value === 'number' && value > 0,
          negative: typeof value === 'number' && value <= 0,
        },

        className,
      )}
    >
      {label && <span className="quote-value-label">{label}</span>}
      <Component
        {...props}
        valueKey={k || valueKey}
        value={value}
        onChange={onValueChange}
        disabled={readOnly}
      />
    </span>
  )
}

function mapStateToProps(state, op) {
  let permit = op.permit || 'info',
    permission = getPermission(state, PERMS[permit]),
    quoteId = op.id || op.quoteId,
    value = getQuoteValue(
      state,
      op.vehicleId,
      quoteId,
      op.k || op.valueKey,
      undefined,
    )

  return {
    ...op,
    id: quoteId,
    permit,
    value: typeof value === 'undefined' ? op.value : value,
    readOnly: op.readOnly || !permission.w,
  }
}

export default connect(
  mapStateToProps,
  { changeQuote },
)(QuoteValue)
